import { useState } from "react";
import { Wrench, Beer, JapaneseYen } from "lucide-react";
import { dailyReportStatusLabelForDriver } from "@carry/contract";
import { Button, cn, Textarea } from "@carry/ui";
import { formatDisplayDate } from "@carry/shared";
import type { StoredDriverSession } from "../auth";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import { LinkCard } from "../components/LinkCard";
import { ReportCard, ReportRow, ReportSectionTitle } from "../components/ReportCard";
import {
  submitDriverDailyReport,
  useDriverDailyReport,
  type DriverAttendance,
  type DriverDailyReport,
  type DriverFetcher,
} from "../hooks";
import { formatHm, type RouteView } from "../lib/normalize";

export function DailyReportScreen({
  session,
  fetcher,
  routeView,
  attendance,
  inspectionDone = false,
  alcoholDone = false,
  onBack,
  onGoInspection,
  onGoAlcohol,
  onGoExpense,
}: {
  session: StoredDriverSession | null;
  fetcher: DriverFetcher;
  routeView: RouteView;
  attendance: DriverAttendance | null;
  /** このセッションで車両点検を完了済みかどうか */
  inspectionDone?: boolean;
  /** このセッションでアルコール検査を完了済みかどうか */
  alcoholDone?: boolean;
  onBack: () => void;
  onGoInspection: () => void;
  onGoAlcohol: () => void;
  onGoExpense: () => void;
}) {
  const { report, loading, error, refetch } = useDriverDailyReport({
    apiBaseUrl: fetcher.apiBaseUrl,
    session,
  });

  // 備考の初期値はサーバ取得値から導出する。useEffect で後追い setState せず、
  // report の識別子を key にして本文を再マウントすることで初期化を 1 回に限定する
  // (編集中の polling 再取得で備考が巻き戻るのを防ぐ)。
  return (
    <DailyReportForm
      key={`${report?.public_id ?? "new"}:${report?.remarks ?? ""}`}
      fetcher={fetcher}
      routeView={routeView}
      attendance={attendance}
      report={report}
      loading={loading}
      error={error}
      refetch={refetch}
      onBack={onBack}
      onGoInspection={onGoInspection}
      onGoAlcohol={onGoAlcohol}
      onGoExpense={onGoExpense}
      inspectionDone={inspectionDone}
      alcoholDone={alcoholDone}
    />
  );
}

function DailyReportForm({
  fetcher,
  routeView,
  attendance,
  report,
  loading,
  error,
  refetch,
  onBack,
  onGoInspection,
  onGoAlcohol,
  onGoExpense,
  inspectionDone,
  alcoholDone,
}: {
  fetcher: DriverFetcher;
  routeView: RouteView;
  attendance: DriverAttendance | null;
  report: DriverDailyReport | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  onBack: () => void;
  onGoInspection: () => void;
  onGoAlcohol: () => void;
  onGoExpense: () => void;
  inspectionDone?: boolean;
  alcoholDone?: boolean;
}) {
  const [remarks, setRemarks] = useState(report?.remarks ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const delivered = routeView.stops.filter(
    (s) => s.kind === "delivery" && s.status === "done",
  ).length;
  const collected = routeView.stops.filter(
    (s) => s.kind === "collect" && s.status === "done",
  ).length;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitDriverDailyReport(fetcher, {
        delivery_count: delivered + collected,
        remarks: remarks || undefined,
      });
      setSubmitted(true);
      await refetch();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header
        title="運転日報 確認"
        sub={`${formatDisplayDate(routeView.date)} · 退勤時に自動提出`}
        showBack
        onBack={onBack}
      />
      <div className="px-3 py-3.5">
        {loading && !report && (
          <div className="p-4 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
            日報を読込中…
          </div>
        )}
        {error && (
          <div className="mb-2.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {error}
          </div>
        )}

        <ReportSectionTitle label="基本情報" hint="勤怠より" />
        <ReportCard>
          {[
            ["日付", formatDisplayDate(routeView.date)],
            ["出勤", attendance?.clock_in_at ? formatHm(attendance.clock_in_at) : "未打刻"],
            ["退勤", attendance?.clock_out_at ? formatHm(attendance.clock_out_at) : "—"],
            ["配達完了", `${delivered} 件`],
            ["回収完了", `${collected} 件`],
            ["状態", report ? dailyReportStatusLabelForDriver(report.status) : "日報未作成"],
          ].map(([k, v], i, a) => (
            <ReportRow key={String(k)} last={i === a.length - 1}>
              <span className="text-[var(--ink-3)]">{k}</span>
              <span className="num font-medium">{v}</span>
            </ReportRow>
          ))}
        </ReportCard>

        <ReportSectionTitle label="点呼・点検" hint="" className="mt-3.5" />
        <div className="grid grid-cols-2 gap-2">
          <LinkCard
            icon={<Wrench size={20} />}
            label="車両点検 (12項目)"
            badge={inspectionDone ? "済" : undefined}
            onClick={onGoInspection}
          />
          <LinkCard
            icon={<Beer size={20} />}
            label="アルコール検査"
            badge={alcoholDone ? "済" : undefined}
            onClick={onGoAlcohol}
          />
        </div>
        <div className="mt-2">
          <LinkCard icon={<JapaneseYen size={20} />} label="経費を追加" onClick={onGoExpense} />
        </div>

        <ReportSectionTitle label="備考" hint="任意" className="mt-3.5" />
        <ReportCard>
          <Textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="特記事項があれば入力してください"
            className="min-h-[80px] resize-y border-0 bg-transparent p-0 text-base text-[var(--ink-2)] shadow-none focus-visible:ring-0"
          />
        </ReportCard>

        {submitError && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {submitError}
          </div>
        )}
        {submitted && (
          <div className="mt-2.5 rounded-lg bg-[var(--st-done-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
            ✓ 備考を保存しました
          </div>
        )}
      </div>
      <BottomBar>
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="h-auto min-h-[var(--row-h)] px-3.5 py-3 text-[length:var(--fs-sm)] text-[var(--ink-3)]"
        >
          閉じる
        </Button>
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={submitting || report?.status === "approved"}
          className={cn(
            "h-auto min-h-[var(--row-h)] flex-1 bg-[var(--ok)] px-4 py-3 text-[length:var(--fs-md)] font-semibold text-white hover:bg-[var(--ok)]/90",
            submitting && "cursor-wait opacity-70",
          )}
        >
          {report?.status === "approved" ? "承認済" : submitting ? "保存中…" : "備考を保存"}
        </Button>
      </BottomBar>
    </div>
  );
}
